// =============================================================================
// pages/EmailCampaign.js — Faz 9b TAM kapanış (2026-04-29)
// =============================================================================
// Eski sürüm: 303 satır, /status /log /control endpoint'leri ile bozuk Python
//            scriptini izliyordu. 4 günde 87 mail (3633 fail) gönderdi.
//
// Yeni sürüm: DB tabanlı + Brevo SMTP relay üzerinden çalışan tam B2B kampanya
//            yönetim modülü. Backend services/email-worker.js her dakika aktif
//            kampanyaları rate_per_hour'a göre batch gönderir.
//
// Bölümler:
//   1. Üst dashboard kartları   — contacts breakdown, son 24h
//   2. Tab — Kampanyalar         — liste + oluştur/düzenle modal + start/pause/log
//   3. Tab — Kontaklar           — paginated, filtreli, sektör segmentasyonu
//
// Tasarım: marka renkleri (#1565C0, #E67E22), kart-bazlı, smooth transitions,
//          mobile-first 360px+, accessible (aria-label, klavye Tab/Esc)
// =============================================================================

import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Typography, Tabs, Tab, Card, CardContent, Grid, Button, IconButton,
  Stack, Chip, TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, LinearProgress,
  Pagination, Tooltip, Slider, Divider
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import HistoryIcon from "@mui/icons-material/History";
import EmailIcon from "@mui/icons-material/Email";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import toast, { Toaster } from "react-hot-toast";
import {
  ec_stats, ec_segments, ec_contacts, ec_contactCreate, ec_contactDelete,
  ec_campaigns, ec_campaign, ec_campaignCreate, ec_campaignUpdate,
  ec_campaignStart, ec_campaignPause, ec_campaignResume, ec_campaignCancel,
  ec_campaignLog, ec_campaignTestSend, ec_campaignFinish
} from "../services/api";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import DoneAllIcon from "@mui/icons-material/DoneAll";

// Reklam hızı preset seçenekleri (saatlik gönderim → kullanıcı dostu açıklama)
// Worker fractional credit destekliyor: rate<60 da düzgün — örn rate=2 → her 30dk 1 mail
const RATE_PRESETS = [
  { v: 2,   label: "Çok yavaş — saatte 2 (her 30 dk 1 mail, günde 48)" },
  { v: 4,   label: "Yavaş — saatte 4 (her 15 dk 1 mail, günde 96)" },
  { v: 12,  label: "Düşük — saatte 12 (her 5 dk 1 mail, günde 288)" },
  { v: 30,  label: "Normal — saatte 30 (günde 720)" },
  { v: 60,  label: "Hızlı — saatte 60 (her dakika 1, günde 1440)" },
  { v: 120, label: "Çok hızlı — saatte 120 (günde 2880)" },
  { v: 300, label: "Maksimum — saatte 300 (Brevo Free günlük limiti)" },
];

// Status -> label/renk
const CAMPAIGN_STATUS = {
  draft:     { label: "Taslak",          color: "default" },
  sending:   { label: "Gönderiliyor",    color: "info"    },
  paused:    { label: "Duraklatıldı",    color: "warning" },
  done:      { label: "Tamamlandı",      color: "success" },
  cancelled: { label: "İptal",           color: "error"   }
};
const CONTACT_STATUS = {
  active:        { label: "Aktif",        color: "default" },
  sent:          { label: "Gönderildi",   color: "success" },
  bounced:       { label: "Bounce",       color: "error"   },
  unsubscribed:  { label: "Abone Çıktı",  color: "warning" },
  invalid:       { label: "Geçersiz",     color: "default" }
};

export default function EmailCampaign() {
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [segments, setSegments] = useState({ sektorler: [], osbler: [] });
  const [campaigns, setCampaigns] = useState([]);
  const [campaignDlg, setCampaignDlg] = useState({ open: false, edit: false, data: null });
  const [logDlg, setLogDlg] = useState({ open: false, campaign: null, lines: [] });
  const [testSendOpen, setTestSendOpen] = useState({ open: false, campId: null, email: "" });
  const [contacts, setContacts] = useState({ items: [], total: 0, page: 1, limit: 50 });
  const [contactFilter, setContactFilter] = useState({ status: "", sektor: "", osb: "", search: "" });
  const [contactAddOpen, setContactAddOpen] = useState(false);
  const [newContact, setNewContact] = useState({ firma_adi: "", email: "", telefon: "", sektor: "", osb: "" });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [busy, setBusy] = useState(false);

  // Veri çekme
  const loadStats = useCallback(async () => {
    try {
      const [s, seg] = await Promise.all([ec_stats(), ec_segments()]);
      setStats(s.data);
      setSegments(seg.data);
    } catch (e) { /* sessiz */ }
  }, []);

  const loadCampaigns = useCallback(async () => {
    try { const r = await ec_campaigns(); setCampaigns(r.data); }
    catch (e) { toast.error("Kampanyalar yüklenemedi: " + e.message); }
  }, []);

  const loadContacts = useCallback(async () => {
    try {
      const params = { page: contacts.page, limit: contacts.limit };
      if (contactFilter.status) params.status = contactFilter.status;
      if (contactFilter.sektor) params.sektor = contactFilter.sektor;
      if (contactFilter.osb)    params.osb = contactFilter.osb;
      if (contactFilter.search) params.search = contactFilter.search;
      const r = await ec_contacts(params);
      setContacts(prev => ({ ...prev, items: r.data.items, total: r.data.total }));
    } catch (e) { toast.error("Kontaklar yüklenemedi"); }
  }, [contacts.page, contacts.limit, contactFilter]);

  useEffect(() => { loadStats(); loadCampaigns(); loadContacts(); }, [loadStats, loadCampaigns, loadContacts]);

  // 30sn otomatik yenileme — sending kampanyada sayaç güncel kalır
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => { loadStats(); loadCampaigns(); }, 30000);
    return () => clearInterval(t);
  }, [autoRefresh, loadStats, loadCampaigns]);

  // Türetilmiş veriler
  const contactBreakdown = useMemo(() => {
    if (!stats?.contacts) return { active: 0, sent: 0, bounced: 0, total: 0 };
    const out = { active: 0, sent: 0, bounced: 0, total: 0 };
    for (const r of stats.contacts) {
      if (r.status in out) out[r.status] = r.n;
      out.total += r.n;
    }
    return out;
  }, [stats]);

  const activeCampaign = useMemo(() => campaigns.find(c => c.status === "sending"), [campaigns]);

  // Kampanya işlemleri
  const openNewCampaign = () => {
    setCampaignDlg({
      open: true, edit: false,
      data: { name: "", subject: "", html_content: "",
        filter_status: "active", filter_sektor: "", filter_osb: "",
        rate_per_hour: 30,        // Normal preset (günde 720)
        keep_alive: 1 }           // Varsayılan: otomatik bitme — kullanıcı manuel kapatır
    });
  };

  const openEditCampaign = async (c) => {
    try { const r = await ec_campaign(c.id); setCampaignDlg({ open: true, edit: true, data: r.data }); }
    catch (e) { toast.error("Kampanya yüklenemedi"); }
  };

  const saveCampaign = async () => {
    const d = campaignDlg.data;
    if (!d.name || !d.subject || !d.html_content) {
      toast.error("Ad, konu ve HTML içerik zorunlu"); return;
    }
    setBusy(true);
    try {
      if (campaignDlg.edit) { await ec_campaignUpdate(d.id, d); toast.success("Reklam güncellendi"); }
      else { await ec_campaignCreate(d); toast.success("Yeni reklam oluşturuldu (taslak)"); }
      setCampaignDlg({ open: false, edit: false, data: null });
      loadCampaigns();
    } catch (e) { toast.error(e?.response?.data?.error || "Kayıt hatası"); }
    finally { setBusy(false); }
  };

  const startCampaign = async (id) => {
    if (!window.confirm("Reklamı başlatmak istiyor musunuz? Filtreye uyan tüm kontaklara mail gönderilir.")) return;
    try {
      const r = await ec_campaignStart(id);
      toast.success(`Reklam başlatıldı (${r.data.total_targets} hedef)`);
      loadCampaigns();
    } catch (e) { toast.error(e?.response?.data?.error || "Başlatılamadı"); }
  };

  const pauseCampaign = async (id) => {
    try { await ec_campaignPause(id); toast.success("Duraklatıldı"); loadCampaigns(); }
    catch (e) { toast.error(e?.response?.data?.error || "Hata"); }
  };
  const resumeCampaign = async (id) => {
    try { await ec_campaignResume(id); toast.success("Devam ediyor"); loadCampaigns(); }
    catch (e) { toast.error(e?.response?.data?.error || "Hata"); }
  };
  const cancelCampaign = async (id) => {
    if (!window.confirm("Reklamı tamamen iptal etmek istiyor musunuz? Tekrar başlatılamaz.")) return;
    try { await ec_campaignCancel(id); toast.success("İptal edildi"); loadCampaigns(); }
    catch (e) { toast.error("Hata"); }
  };

  // Manuel "Tamamla" — keep_alive=1 olan reklamlar otomatik bitmez, kullanıcı bitirir
  const finishCampaign = async (id) => {
    if (!window.confirm("Reklamı şimdi tamamla? (Statü 'Tamamlandı' olur, gönderim durur)")) return;
    try { await ec_campaignFinish(id); toast.success("Tamamlandı"); loadCampaigns(); }
    catch (e) { toast.error(e?.response?.data?.error || "Hata"); }
  };

  const showLog = async (c) => {
    try { const r = await ec_campaignLog(c.id, 200); setLogDlg({ open: true, campaign: c, lines: r.data }); }
    catch (e) { toast.error("Log yüklenemedi"); }
  };

  const doTestSend = async () => {
    const { campId, email } = testSendOpen;
    if (!email || !email.includes("@")) { toast.error("Geçerli e-posta girin"); return; }
    setBusy(true);
    try {
      const r = await ec_campaignTestSend(campId, email);
      toast.success(`Test mail gönderildi: ${email}`);
      setTestSendOpen({ open: false, campId: null, email: "" });
    } catch (e) { toast.error(e?.response?.data?.error || "Test mail başarısız"); }
    finally { setBusy(false); }
  };

  const addContact = async () => {
    if (!newContact.email) { toast.error("E-posta zorunlu"); return; }
    try {
      await ec_contactCreate(newContact);
      toast.success("Kontak eklendi");
      setContactAddOpen(false);
      setNewContact({ firma_adi: "", email: "", telefon: "", sektor: "", osb: "" });
      loadContacts(); loadStats();
    } catch (e) { toast.error(e?.response?.data?.error || "Hata"); }
  };

  const delContact = async (id) => {
    if (!window.confirm("Kontağı silmek istiyor musunuz?")) return;
    try { await ec_contactDelete(id); toast.success("Silindi"); loadContacts(); loadStats(); }
    catch (e) { toast.error("Hata"); }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <Toaster />

      {/* ============== BAŞLIK ============== */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "#1565C0" }}>
            📧 Email Reklam Yönetimi
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Brevo SMTP relay · Worker her dakika batch gönderir · {contactBreakdown.total} kontak
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={() => setAutoRefresh(v => !v)}
            startIcon={<RefreshIcon />} color={autoRefresh ? "primary" : "inherit"}>
            {autoRefresh ? "Oto açık" : "Oto kapalı"}
          </Button>
          <Button size="small" variant="contained"
            onClick={() => { loadStats(); loadCampaigns(); loadContacts(); }} startIcon={<RefreshIcon />}>
            Yenile
          </Button>
        </Stack>
      </Stack>

      {/* ============== ÜST KARTLAR ============== */}
      <Grid container spacing={2} mb={2}>
        <StatCard icon={<GroupIcon />} color="#1565C0" label="Aktif Kontak" value={contactBreakdown.active} sub={`${contactBreakdown.total} toplam`} />
        <StatCard icon={<CheckCircleIcon />} color="#10b981" label="Gönderilen" value={contactBreakdown.sent} sub="başarılı" />
        <StatCard icon={<ErrorIcon />} color="#ef4444" label="Bounce" value={contactBreakdown.bounced} sub="3+ hata sonrası" />
        <StatCard icon={<EmailIcon />} color="#E67E22" label="Son 24 Saat" value={stats?.last_24h?.sent || 0} sub={`${stats?.last_24h?.failed || 0} hata`} />
      </Grid>

      {/* Aktif kampanya bandı */}
      {activeCampaign && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<SendIcon />}>
          <strong>Aktif reklam:</strong> {activeCampaign.name} — {activeCampaign.sent_count}/{activeCampaign.total_targets} gönderildi
          ({Math.round((activeCampaign.sent_count / Math.max(1, activeCampaign.total_targets)) * 100)}%, hız {activeCampaign.rate_per_hour}/saat
          {activeCampaign.keep_alive ? " · süresiz mod" : ""})
          <LinearProgress variant="determinate"
            value={(activeCampaign.sent_count / Math.max(1, activeCampaign.total_targets)) * 100}
            sx={{ mt: 1, height: 6, borderRadius: 3 }} />
        </Alert>
      )}

      {/* ============== TABS ============== */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
          <Tab icon={<EmailIcon />} iconPosition="start" label={`Reklamlar (${campaigns.length})`} />
          <Tab icon={<GroupIcon />} iconPosition="start" label={`Kontaklar (${contactBreakdown.total})`} />
        </Tabs>
      </Paper>

      {/* ============== TAB 1: KAMPANYALAR ============== */}
      {tab === 0 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Reklamlar</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNewCampaign}>Yeni Reklam</Button>
          </Stack>

          {campaigns.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
              Henüz reklam yok. <Button onClick={openNewCampaign}>İlkini oluştur</Button>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {campaigns.map(c => {
                const st = CAMPAIGN_STATUS[c.status] || { label: c.status, color: "default" };
                const pct = c.total_targets > 0 ? (c.sent_count / c.total_targets) * 100 : 0;
                return (
                  <Grid item xs={12} md={6} key={c.id}>
                    <Card sx={{ borderLeft: `4px solid ${c.status === "sending" ? "#1565C0" : "#e5e7eb"}` }}>
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" mb={1}>
                          <Typography fontWeight={700} sx={{ color: "#1a3d6d" }}>{c.name}</Typography>
                          <Chip label={st.label} color={st.color} size="small" />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" mb={1.5} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          ✉ {c.subject}
                        </Typography>
                        <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap">
                          <Chip size="small" label={`Hedef: ${c.total_targets}`} />
                          <Chip size="small" color="success" label={`✓ ${c.sent_count}`} />
                          {c.failed_count > 0 && <Chip size="small" color="error" label={`✗ ${c.failed_count}`} />}
                          <Chip size="small" variant="outlined" label={`${c.rate_per_hour}/saat`} />
                          {c.keep_alive == 1 && c.status !== "done" && c.status !== "cancelled" && (
                            <Tooltip title="Süresiz mod — kontaklar bittiğinde otomatik kapanmaz, manuel tamamlamanız gerekir"><Chip size="small" color="info" variant="outlined" label="♾ süresiz" /></Tooltip>
                          )}
                          {c.filter_sektor && <Chip size="small" variant="outlined" label={c.filter_sektor} />}
                        </Stack>
                        {c.total_targets > 0 && (
                          <LinearProgress variant="determinate" value={pct} sx={{ mb: 1.5, height: 6, borderRadius: 3 }} />
                        )}
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {c.status === "draft" && (
                            <Button size="small" variant="contained" startIcon={<PlayArrowIcon />}
                              onClick={() => startCampaign(c.id)} color="success">Başlat</Button>
                          )}
                          {c.status === "sending" && (
                            <Button size="small" variant="contained" startIcon={<PauseIcon />}
                              onClick={() => pauseCampaign(c.id)} color="warning">Duraklat</Button>
                          )}
                          {c.status === "paused" && (
                            <Button size="small" variant="contained" startIcon={<PlayArrowIcon />}
                              onClick={() => resumeCampaign(c.id)} color="success">Devam</Button>
                          )}
                          {/* Sending dahil her aktif statüde Ayarlar açılır — backend rate ve keep_alive
                              alanlarını canlı değiştirebilir; ad/konu/içerik için pause gerekir */}
                          {c.status !== "done" && c.status !== "cancelled" && (
                            <Button size="small" variant="outlined" startIcon={<EditIcon />}
                              onClick={() => openEditCampaign(c)}>Ayarlar</Button>
                          )}
                          <Button size="small" variant="outlined" startIcon={<SendIcon />}
                            onClick={() => setTestSendOpen({ open: true, campId: c.id, email: "" })}>Test</Button>
                          <Button size="small" variant="outlined" startIcon={<HistoryIcon />}
                            onClick={() => showLog(c)}>Log</Button>
                          {/* Manuel "Tamamla" — keep_alive durumlarında kullanıcı bitirme yetkisi */}
                          {(c.status === "sending" || c.status === "paused") && (
                            <Tooltip title="Bu reklamı şimdi tamamla (gönderim durur, statü 'Tamamlandı' olur)">
                              <Button size="small" variant="outlined" color="success" startIcon={<DoneAllIcon />}
                                onClick={() => finishCampaign(c.id)}>Tamamla</Button>
                            </Tooltip>
                          )}
                          {c.status !== "done" && c.status !== "cancelled" && (
                            <Tooltip title="Tamamen iptal — geri alınamaz">
                              <IconButton size="small" color="error"
                                onClick={() => cancelCampaign(c.id)}><StopIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* ============== TAB 2: KONTAKLAR ============== */}
      {tab === 1 && (
        <Box>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} mb={2}>
            <TextField size="small" placeholder="Ara: firma, email, sektör..."
              value={contactFilter.search}
              onChange={e => setContactFilter(f => ({ ...f, search: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && loadContacts()}
              sx={{ flex: 1 }} />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Durum</InputLabel>
              <Select value={contactFilter.status} label="Durum"
                onChange={e => setContactFilter(f => ({ ...f, status: e.target.value }))}>
                <MenuItem value="">Tümü</MenuItem>
                {Object.entries(CONTACT_STATUS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sektör</InputLabel>
              <Select value={contactFilter.sektor} label="Sektör"
                onChange={e => setContactFilter(f => ({ ...f, sektor: e.target.value }))}>
                <MenuItem value="">Tümü</MenuItem>
                {segments.sektorler.map(s => (
                  <MenuItem key={s.sektor} value={s.sektor}>{s.sektor} ({s.n})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={loadContacts} startIcon={<RefreshIcon />}>Filtrele</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setContactAddOpen(true)}>Yeni</Button>
          </Stack>

          <TableContainer component={Paper} sx={{ maxHeight: "calc(100vh - 380px)" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["Firma", "Email", "Telefon", "Sektör/OSB", "Durum", "Son Gönderim", ""].map(h => (
                    <TableCell key={h} sx={{ bgcolor: "#f5f5f5", fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.items.map(c => {
                  const cs = CONTACT_STATUS[c.status] || { label: c.status, color: "default" };
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell>{c.firma_adi || "-"}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>{c.email}</TableCell>
                      <TableCell>{c.telefon || "-"}</TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">{c.sektor || "-"}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.osb || ""}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" label={cs.label} color={cs.color} /></TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {c.last_sent_at ? new Date(c.last_sent_at).toLocaleString("tr-TR") : "-"}
                        {c.bounce_count > 0 && <Typography variant="caption" color="error" display="block">{c.bounce_count} bounce</Typography>}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => delContact(c.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
            <Typography variant="caption">{contacts.total} kontak · {contacts.items.length} gösteriliyor</Typography>
            <Pagination count={Math.ceil(contacts.total / contacts.limit)}
              page={contacts.page} onChange={(_, v) => setContacts(prev => ({ ...prev, page: v }))} size="small" />
          </Stack>
        </Box>
      )}

      {/* ============== REKLAM OLUŞTUR/DÜZENLE MODAL ============== */}
      <Dialog open={campaignDlg.open} onClose={() => setCampaignDlg({ open: false })} maxWidth="md" fullWidth>
        <DialogTitle>{campaignDlg.edit ? "Reklam Ayarları" : "Yeni Reklam"}</DialogTitle>
        <DialogContent dividers>
          {campaignDlg.data && (
            <Stack spacing={2} mt={1}>
              <TextField label="Reklam Adı *" fullWidth value={campaignDlg.data.name || ""}
                onChange={e => setCampaignDlg(d => ({ ...d, data: { ...d.data, name: e.target.value } }))} />
              <TextField label="Mail Konusu *" fullWidth value={campaignDlg.data.subject || ""}
                onChange={e => setCampaignDlg(d => ({ ...d, data: { ...d.data, subject: e.target.value } }))} />
              <TextField label="HTML İçerik * — placeholder'lar: {{FIRMA_ADI}}, {{EMAIL}}, {{SEKTOR}}, {{OSB}}"
                fullWidth multiline minRows={8} maxRows={20}
                value={campaignDlg.data.html_content || ""}
                onChange={e => setCampaignDlg(d => ({ ...d, data: { ...d.data, html_content: e.target.value } }))}
                sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12 } }} />
              <Divider><Chip label="Filtre — kimlere gönderilsin?" size="small" /></Divider>
              <Stack direction="row" spacing={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Kontak Durumu</InputLabel>
                  <Select label="Kontak Durumu" value={campaignDlg.data.filter_status || ""}
                    onChange={e => setCampaignDlg(d => ({ ...d, data: { ...d.data, filter_status: e.target.value } }))}>
                    <MenuItem value="active">Sadece Aktif (önerilen)</MenuItem>
                    <MenuItem value="sent">Gönderilmişler</MenuItem>
                    <MenuItem value="">Tümü</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Sektör (boş=tümü)</InputLabel>
                  <Select label="Sektör (boş=tümü)" value={campaignDlg.data.filter_sektor || ""}
                    onChange={e => setCampaignDlg(d => ({ ...d, data: { ...d.data, filter_sektor: e.target.value } }))}>
                    <MenuItem value="">Tümü</MenuItem>
                    {segments.sektorler.map(s => <MenuItem key={s.sektor} value={s.sektor}>{s.sektor} ({s.n})</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>

              <Divider><Chip label="Gönderim Hızı" size="small" /></Divider>
              {/* Hız preset dropdown — kullanıcı dostu açıklamalar; "her 30dk 1 mail" gibi.
                  Worker fractional credit destekler, rate=2 olsa bile düzgün çalışır */}
              <FormControl fullWidth size="small">
                <InputLabel>Gönderim hızı (preset)</InputLabel>
                <Select label="Gönderim hızı (preset)"
                  value={RATE_PRESETS.find(p => p.v === campaignDlg.data.rate_per_hour)?.v || ""}
                  onChange={e => setCampaignDlg(d => ({ ...d, data: { ...d.data, rate_per_hour: e.target.value } }))}>
                  {RATE_PRESETS.map(p => <MenuItem key={p.v} value={p.v}>{p.label}</MenuItem>)}
                </Select>
              </FormControl>
              {/* Manuel ince ayar — preset uymazsa kullanıcı 1-300 arası serbest girer */}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Manuel hız: <strong>{campaignDlg.data.rate_per_hour}</strong> mail/saat
                  ({campaignDlg.data.rate_per_hour < 60
                    ? `her ${Math.round(60 / campaignDlg.data.rate_per_hour)} dakikada 1 mail`
                    : `dakikada ~${(campaignDlg.data.rate_per_hour / 60).toFixed(1)} mail`})
                </Typography>
                <Slider min={1} max={300} step={1}
                  value={campaignDlg.data.rate_per_hour}
                  onChange={(_, v) => setCampaignDlg(d => ({ ...d, data: { ...d.data, rate_per_hour: v } }))}
                  marks={[{value:1,label:"1"},{value:30,label:"30"},{value:60,label:"60"},{value:300,label:"300"}]} />
              </Box>

              <Divider><Chip label="Bitirme Modu" size="small" /></Divider>
              {/* keep_alive: kontaklar bittiğinde otomatik 'done' OLMASIN — kullanıcı manuel bitirir
                  Faydası: yeni kontak eklendiğinde tekrar başlatmaya gerek yok, kendisi devam eder */}
              <FormControlLabel
                control={
                  <Switch
                    checked={!!campaignDlg.data.keep_alive}
                    onChange={e => setCampaignDlg(d => ({ ...d, data: { ...d.data, keep_alive: e.target.checked ? 1 : 0 } }))}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Süresiz mod — otomatik tamamlanmasın</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Açıkken: tüm kontaklar gönderildikten sonra reklam çalışır kalır.
                      Yeni kontak eklendiğinde otomatik onlara da gider. Bitirmek için "Tamamla" butonu.
                    </Typography>
                  </Box>
                }
              />

              <Alert severity="info" sx={{ fontSize: 12 }}>
                <strong>Brevo Free</strong> günde 300 mail. Lite/Premium daha yüksek.
                Aktif gönderim sırasında: hız ve süresiz mod canlı değiştirilebilir, ad/konu/içerik için önce duraklatın.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDlg({ open: false })}>İptal</Button>
          <Button variant="contained" onClick={saveCampaign} disabled={busy}>
            {busy ? "Kaydediliyor..." : (campaignDlg.edit ? "Kaydet" : "Oluştur")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============== TEST MAIL MODAL ============== */}
      <Dialog open={testSendOpen.open} onClose={() => setTestSendOpen({ open: false })} maxWidth="xs" fullWidth>
        <DialogTitle>Test Mail Gönder</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary">
            Sadece tek adrese gönderir, kontak listesini etkilemez. Placeholder'lar test verisiyle doldurulur.
          </Typography>
          <TextField fullWidth autoFocus margin="normal" label="Hedef e-posta"
            value={testSendOpen.email}
            onChange={e => setTestSendOpen(s => ({ ...s, email: e.target.value }))}
            placeholder="ornek@firma.com" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestSendOpen({ open: false })}>İptal</Button>
          <Button variant="contained" onClick={doTestSend} disabled={busy} startIcon={<SendIcon />}>Gönder</Button>
        </DialogActions>
      </Dialog>

      {/* ============== LOG MODAL ============== */}
      <Dialog open={logDlg.open} onClose={() => setLogDlg({ open: false, lines: [] })} maxWidth="md" fullWidth>
        <DialogTitle>Gönderim Logu — {logDlg.campaign?.name}</DialogTitle>
        <DialogContent dividers>
          {logDlg.lines.length === 0 ? (
            <Typography color="text.secondary">Henüz log yok</Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Tarih</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>Mesaj</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logDlg.lines.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontSize: 11 }}>{new Date(r.sent_at).toLocaleString("tr-TR")}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{r.email}</TableCell>
                      <TableCell><Chip size="small" label={r.status} color={r.status === "sent" ? "success" : "error"} /></TableCell>
                      <TableCell sx={{ fontSize: 11, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {r.error_message || r.smtp_response || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDlg({ open: false, lines: [] })}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* ============== KONTAK EKLE MODAL ============== */}
      <Dialog open={contactAddOpen} onClose={() => setContactAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Yeni Kontak</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Firma Adı" fullWidth value={newContact.firma_adi}
              onChange={e => setNewContact(c => ({ ...c, firma_adi: e.target.value }))} />
            <TextField label="E-posta *" fullWidth value={newContact.email}
              onChange={e => setNewContact(c => ({ ...c, email: e.target.value }))} />
            <TextField label="Telefon" fullWidth value={newContact.telefon}
              onChange={e => setNewContact(c => ({ ...c, telefon: e.target.value }))} />
            <TextField label="Sektör" fullWidth value={newContact.sektor}
              onChange={e => setNewContact(c => ({ ...c, sektor: e.target.value }))} />
            <TextField label="OSB" fullWidth value={newContact.osb}
              onChange={e => setNewContact(c => ({ ...c, osb: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactAddOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={addContact}>Ekle</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Üst dashboard kartı
function StatCard({ icon, color, label, value, sub }) {
  return (
    <Grid item xs={6} sm={3}>
      <Card sx={{ borderTop: `3px solid ${color}` }}>
        <CardContent sx={{ "&:last-child": { pb: 2 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ color: color, fontSize: 28, display: "flex" }}>{icon}</Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>{Number(value).toLocaleString("tr-TR")}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
              {sub && <Typography variant="caption" color="text.disabled" display="block">{sub}</Typography>}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}
